import React from "react";
import { ChevronRight, UploadCloud } from "lucide-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { allCountries, allJpLevels, allJpVisaTypes } from "./allData";
import { PostAxios } from "../../helpers/axiosHelpers";
import { toast } from "sonner";
import { useLanguage } from "../../contexts/LanguageContext";

const RegisterForm = ({ sectionData = null }) => {
  const { t, language } = useLanguage();
  const pick = (en, ja, key) => (language === "ja" && ja) ? ja : (en ?? t(key));
  const validationSchema = Yup.object({
    firstName: Yup.string().trim().required("First name is required"),
    lastName: Yup.string().trim().required("Last name is required"),
    dob: Yup.date()
      .required("Date of birth is required")
      .min(new Date(1900, 0, 1), "Date of birth is too far in the past")
      .max(new Date(), "Date of birth cannot be in the future"),
    gender: Yup.string().required("Gender is required"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    phone: Yup.string()
      .matches(/^[0-9-]+$/, "Must be a valid phone number")
      .required("Phone number is required"),
    nationality: Yup.string()
      .notOneOf(["Select Country"], "Please select a country")
      .required("Nationality is required"),
    visaType: Yup.string()
      .notOneOf(["Select Visa"], "Please select visa")
      .required("Visa type is required"),
    visaExpiry: Yup.date().required("Visa expiry date is required"),
    japaneseLevel: Yup.string()
      .notOneOf(["Select Level"], "Please select level")
      .required("Japanese level is required"),
    resume: Yup.mixed().required("A resume is required"),
  });

  // 2. Initialize Formik
  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      dob: "",
      gender: "",
      email: "",
      phone: "",
      nationality: "",
      visaType: "",
      visaExpiry: "",
      japaneseLevel: "",
      resume: null,
    },
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      // 1. Prepare Multipart Form Data for the file upload
      const data = new FormData();
      Object.keys(values).forEach((key) => {
        data.append(key, values[key]);
      });
      // 2. Wrap the Axios call in toast.promise
      toast.promise(
        PostAxios("/application/submit", data, {
          headers: {
            "Content-Type": undefined,
          },
        }),
        {
          loading: "Creating your profile...",
          success: (res) => {
            resetForm();
            setSubmitting(false); // Button will re-enable
            return `Welcome, ${values.firstName}! Registration successful.`;
          },
          error: (err) => {
            setSubmitting(false); // Button will re-enable even on error
            const errorMessage =
              err.response?.data?.message || "Registration failed.";
            return errorMessage;
          },
        },
      );
    },
  });
  // Helper to render error messages
  const ErrorMsg = ({ name }) =>
    formik.touched[name] && formik.errors[name] ? (
      <div className="text-[10px] text-red-500 font-bold mt-1 uppercase italic">
        {formik.errors[name]}
      </div>
    ) : null;

  return (
    <section className="py-16 bg-slate-50" id="register">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Process Guide — Japanese UX: always show the journey before the form */}
        <div className="mb-6 grid grid-cols-3 gap-3">
          {[
            { num: "01", label: t("register.process.step1") },
            { num: "02", label: t("register.process.step2") },
            { num: "03", label: t("register.process.step3") },
          ].map((step, i, arr) => (
            <div key={step.num} className="flex items-center gap-2">
              <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-4 py-3 flex-1 shadow-sm">
                <span className="w-7 h-7 rounded-full bg-subidha-blue text-white flex items-center justify-center text-[11px] font-black shrink-0">{step.num}</span>
                <span className="text-xs font-bold text-slate-700">{step.label}</span>
              </div>
              {i < arr.length - 1 && (
                <div className="w-4 h-0.5 bg-slate-200 shrink-0"></div>
              )}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-[2rem] p-8 lg:p-12 shadow-2xl border border-slate-100 overflow-hidden relative">
          <div className="mb-10 relative z-10">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-1 bg-accent-green"></div>
                <span className="text-xs font-black text-accent-green uppercase tracking-[0.3em]">
                  {pick(sectionData?.labelEn, sectionData?.labelJa, "register.badge")}
                </span>
              </div>
              {/* Trust badges — Japanese design: certifications near CTAs */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="flex items-center gap-1 bg-blue-50 border border-blue-100 text-subidha-blue text-[10px] font-bold px-2.5 py-1.5 rounded-lg">
                  🔒 {t("register.trust.ssl")}
                </span>
                <span className="flex items-center gap-1 bg-green-50 border border-green-100 text-green-700 text-[10px] font-bold px-2.5 py-1.5 rounded-lg">
                  ✓ {t("register.trust.licensed")}
                </span>
                <span className="flex items-center gap-1 bg-slate-50 border border-slate-200 text-slate-600 text-[10px] font-bold px-2.5 py-1.5 rounded-lg">
                  🛡 {t("register.trust.privacy")}
                </span>
              </div>
            </div>
            <h2 className="text-3xl lg:text-5xl font-serif-brand font-black text-slate-900 mb-3 mt-4">
              {pick(sectionData?.titleEn, sectionData?.titleJa, "register.title")}
            </h2>
            <p className="text-slate-500 font-medium max-w-2xl text-sm">
              {pick(sectionData?.subtitleEn, sectionData?.subtitleJa, "register.subtitle")}
            </p>
          </div>

          <form
            className="space-y-10 relative z-10"
            onSubmit={formik.handleSubmit}
          >
            <div className="space-y-6">
           
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                    {t("register.firstName")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="firstName"
                    {...formik.getFieldProps("firstName")}
                    type="text"
                    className={`w-full px-5 py-4 bg-slate-50 border rounded-2xl outline-none transition-all shadow-sm ${formik.touched.firstName && formik.errors.firstName ? "border-red-500 ring-4 ring-red-500/10" : "border-slate-200 focus:ring-4 focus:ring-accent-green/20 focus:border-accent-green"}`}
                    placeholder="John"
                  />
                  <ErrorMsg name="firstName" />
                </div>
                <div className="space-y-1.5 lg:col-span-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                    {t("register.lastName")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    {...formik.getFieldProps("lastName")}
                    className={`w-full px-5 py-4 bg-slate-50 border rounded-2xl outline-none transition-all shadow-sm ${formik.touched.lastName && formik.errors.lastName ? "border-red-500 ring-4 ring-red-500/10" : "border-slate-200 focus:ring-4 focus:ring-accent-green/20 focus:border-accent-green"}`}
                    placeholder="Doe"
                  />
                  <ErrorMsg name="lastName" />
                </div>
                <div className="space-y-1.5 lg:col-span-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                    {t("register.dob")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="dob"
                    {...formik.getFieldProps("dob")}
                    type="date"
                    max={new Date().toISOString().split("T")[0]}
                    className={`w-full px-5 py-4 bg-slate-50 border rounded-2xl outline-none transition-all shadow-sm ${formik.touched.dob && formik.errors.dob ? "border-red-500 ring-4 ring-red-500/10" : "border-slate-200 focus:ring-4 focus:ring-accent-green/20 focus:border-accent-green"}`}
                  />
                  <ErrorMsg name="dob" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1 block">
                    {t("register.gender")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center space-x-6 h-[58px] px-2">
                    {["Male", "Female", "Other"].map((option) => (
                      <label
                        key={option}
                        className="flex items-center space-x-2 cursor-pointer group"
                      >
                        <input
                          type="radio"
                          name="gender"
                          value={option}
                          checked={formik.values.gender === option}
                          onChange={formik.handleChange}
                          className="w-5 h-5 text-accent-green border-slate-300 focus:ring-accent-green"
                        />
                        <span className="text-sm font-bold text-slate-600 group-hover:text-subidha-blue">
                          {t(`register.${option.toLowerCase()}`)}
                        </span>
                      </label>
                    ))}
                  </div>
                  <ErrorMsg name="gender" />
                </div>
              </div>
            </div>

            {/* SECTION 02: Contact & Account */}
            <div className="space-y-6">

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                    {t("register.email")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...formik.getFieldProps("email")}
                    type="email"
                    className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-accent-green/20 focus:border-accent-green outline-none transition-all shadow-sm"
                    placeholder="email@example.com"
                  />
                  <ErrorMsg name="email" />
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                    {t("register.phone")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...formik.getFieldProps("phone")}
                    type="tel"
                    className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-accent-green/20 focus:border-accent-green outline-none transition-all shadow-sm"
                    placeholder="080-1234-5678"
                  />
                  <ErrorMsg name="phone" />
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                    {t("register.nationality")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...formik.getFieldProps("nationality")}
                    className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-accent-green/20 focus:border-accent-green outline-none appearance-none transition-all shadow-sm cursor-pointer"
                  >
                    <option value="">Select Country</option>
                    {allCountries.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                  <ErrorMsg name="nationality" />
                </div>
              </div>
            </div>
            <div className="space-y-6">
              <div className="grid md:grid-cols-3 gap-6">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                    {t("register.visaType")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...formik.getFieldProps("visaType")}
                    className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-accent-green/20 focus:border-accent-green outline-none appearance-none transition-all shadow-sm cursor-pointer"
                  >
                    <option>Select Visa</option>
                    {allJpVisaTypes.map((visa) => (
                      <option key={visa.code} value={visa.code}>
                        {visa.name}
                      </option>
                    ))}
                  </select>
                  <ErrorMsg name="visaType" />
                </div>
                <div className="space-y-1.5 lg:col-span-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                    {t("register.visaExpiry")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    {...formik.getFieldProps("visaExpiry")}
                    className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-accent-green/20 focus:border-accent-green outline-none transition-all shadow-sm"
                  />
                  <ErrorMsg name="visaExpiry" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                    {t("register.japaneseLevel")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...formik.getFieldProps("japaneseLevel")}
                    className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-accent-green/20 focus:border-accent-green outline-none appearance-none transition-all shadow-sm cursor-pointer"
                  >
                    <option>Select Level</option>
                    {allJpLevels.map((level) => (
                      <option key={level.code} value={level.code}>
                        {level.name}
                      </option>
                    ))}
                  </select>
                  <ErrorMsg name="japaneseLevel" />
                </div>
              </div>
            </div>

            {/* SECTION 04: Upload Documents (Special Handling for Files) */}
            <div className="space-y-6">
       
              <div className="relative group">
                <div
                  className={`w-full p-12 border-4 border-dashed rounded-[2.5rem] flex flex-col items-center justify-center space-y-4 transition-all duration-300 cursor-pointer ${formik.errors.resume ? "border-red-200 bg-red-50/30" : "border-slate-100 bg-slate-50/50 hover:border-accent-green hover:bg-white"}`}
                >
                  <div
                    className={`w-20 h-20 rounded-3xl flex items-center justify-center shadow-xl border ${formik.values.resume ? "bg-accent-green text-white" : "bg-white text-slate-300"}`}
                  >
                    <UploadCloud className="w-10 h-10" />
                  </div>
                  <div className="text-center">
                    <p className="text-base font-black text-slate-700">
                      {formik.values.resume
                        ? formik.values.resume.name
                        : t("register.uploadResume")}
                    </p>
                    <ErrorMsg name="resume" />
                  </div>
                  <input
                    type="file"
                    className="absolute inset-0 opacity-0 cursor-pointer"
                    onChange={(event) => {
                      formik.setFieldValue(
                        "resume",
                        event.currentTarget.files[0],
                      );
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="pt-8 border-t border-slate-100 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest max-w-sm">
                  By registering, you agree to Subidha Inc.'s{" "}
                  <span className="text-subidha-blue underline">
                    Privacy Policy
                  </span>
                  .
                </p>
                <p className="text-[10px] text-slate-400 font-medium mt-1">
                  🔒 {t("register.trust.dataProtected")}
                </p>
              </div>
              <button
                type="submit"
                className="w-full md:w-auto bg-subidha-blue hover:bg-slate-900 text-white px-12 py-5 rounded-full font-black text-base transition-all shadow-2xl shadow-blue-900/20 transform hover:scale-105 active:scale-95 flex items-center justify-center space-x-4"
                disabled={formik.isSubmitting || !formik.isValid}
              >
                {formik.isSubmitting
                  ? t("register.submitting")
                  : t("register.submitButton")}
                <ChevronRight className="w-6 h-6 ml-2" />
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default RegisterForm;
